import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

const STATUS_LABELS = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  info_requested: 'Info solicitada',
  consumed: 'Consumida',
};

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  info_requested: 'bg-blue-100 text-blue-800',
  consumed: 'bg-purple-100 text-purple-800',
};

const AdminOnboardingPanel = () => {
  const [tab, setTab] = useState('requests');
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [codes, setCodes] = useState([]);
  const [tree, setTree] = useState(null);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [modal, setModal] = useState(null); // { type: 'approve'|'reject'|'info', req }
  const [modalForm, setModalForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [backfillResult, setBackfillResult] = useState(null);

  const showToast = (m, kind = 'info') => {
    setToast({ m, kind });
    setTimeout(() => setToast(null), 3200);
  };

  const load = useCallback(async () => {
    try {
      const [s, r, c, g] = await Promise.all([
        axios.get(`${API}/onboarding/admin/stats`),
        axios.get(`${API}/onboarding/admin/requests${filter !== 'all' ? `?status=${filter}` : ''}`),
        axios.get(`${API}/onboarding/admin/codes`),
        axios.get(`${API}/onboarding/admin/genealogy`),
      ]);
      setStats(s.data);
      setRequests(r.data.requests || []);
      setCodes(c.data.codes || []);
      setTree(g.data.tree);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Error cargando datos', 'error');
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openApprove = (req) => { setModal({ type: 'approve', req }); setModalForm({ admin_notes: '', expires_days: 30, max_uses: 1 }); };
  const openReject = (req) => { setModal({ type: 'reject', req }); setModalForm({ reason: '', admin_notes: '' }); };
  const openInfo = (req) => { setModal({ type: 'info', req }); setModalForm({ message: '', admin_notes: '' }); };
  const closeModal = () => { setModal(null); setModalForm({}); };

  const submitModal = async () => {
    if (!modal) return;
    setBusy(true);
    try {
      const url = `${API}/onboarding/admin/requests/${modal.req.id}/${modal.type === 'approve' ? 'approve' : modal.type === 'reject' ? 'reject' : 'request-info'}`;
      const r = await axios.post(url, modalForm);
      if (modal.type === 'approve') {
        showToast(`Codigo generado: ${r.data.code}. Se envio automaticamente por correo al solicitante.`, 'success');
        try { await navigator.clipboard.writeText(r.data.code); } catch (e) { /* clipboard may be unavailable */ }
      } else {
        showToast('Solicitud actualizada', 'success');
      }
      closeModal();
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Error', 'error');
    } finally {
      setBusy(false);
    }
  };

  const viewDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/onboarding/admin/requests/${id}`);
      setDetail(r.data);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Error', 'error');
    }
  };

  const revokeCode = async (code_id) => {
    if (!window.confirm('¿Revocar este codigo? Solo funciona si no ha sido utilizado.')) return;
    try {
      await axios.post(`${API}/onboarding/admin/codes/${code_id}/revoke`);
      showToast('Codigo revocado', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Error', 'error');
    }
  };

  const backfillFounders = async () => {
    if (!window.confirm('Marcar a todos los usuarios existentes sin datos de incorporacion como fundadores (Generacion 0)?')) return;
    try {
      const r = await axios.post(`${API}/onboarding/admin/founders/backfill`);
      setBackfillResult(r.data);
      showToast(r.data.message || 'OK', 'success');
      await load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Error', 'error');
    }
  };

  // ----- Render arbol -----
  const renderTreeNode = (node, depth = 0) => (
    <div key={node.id || node.email} className="ml-4 mt-2">
      <div className="flex items-center gap-2 text-sm">
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
          G{node.generation ?? '-'}
        </span>
        <span className="font-medium text-gray-800">{node.name}</span>
        <span className="text-gray-400 text-xs">{node.email}</span>
      </div>
      {node.children && node.children.length > 0 && (
        <div className="border-l border-gray-200 pl-3 mt-1">
          {node.children.map((c) => renderTreeNode(c, depth + 1))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-onboarding-panel">
      {toast && (
        <div className={`fixed top-16 right-4 z-50 px-4 py-2 rounded shadow-lg text-sm ${
          toast.kind === 'success' ? 'bg-emerald-600 text-white' :
          toast.kind === 'error' ? 'bg-red-600 text-white' : 'bg-slate-800 text-white'
        }`}>{toast.m}</div>
      )}

      {/* Metricas */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="onboarding-stats">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Politica vigente</p>
            <p className="text-lg font-bold text-purple-700">{stats.policy}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Pendientes</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.requests.pending}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Aprobadas</p>
            <p className="text-2xl font-bold text-emerald-700">{stats.requests.approved}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Consumidas</p>
            <p className="text-2xl font-bold text-purple-700">{stats.requests.consumed}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Rechazadas</p>
            <p className="text-2xl font-bold text-red-700">{stats.requests.rejected}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Codigos activos</p>
            <p className="text-2xl font-bold text-blue-700">{stats.codes.active}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Fundadores (G0)</p>
            <p className="text-2xl font-bold text-slate-700">{stats.users.founders_gen0}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-xs text-gray-500">Total usuarios</p>
            <p className="text-2xl font-bold text-slate-700">{stats.users.total}</p>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex space-x-2 border-b">
        {[
          { id: 'requests', label: 'Solicitudes' },
          { id: 'codes', label: 'Codigos' },
          { id: 'tree', label: 'Genealogia' },
          { id: 'tools', label: 'Herramientas' },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`onboarding-subtab-${t.id}`}
            className={`px-3 py-2 text-sm font-medium rounded-t-lg ${tab === t.id ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500' : 'text-gray-600 hover:text-gray-900'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* --- Solicitudes --- */}
      {tab === 'requests' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Filtrar:</span>
            {['all', 'pending', 'info_requested', 'approved', 'consumed', 'rejected'].map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                data-testid={`onboarding-filter-${s}`}
                className={`text-xs px-2 py-1 rounded ${filter === s ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                {s === 'all' ? 'Todas' : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full text-sm" data-testid="onboarding-requests-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Nombre</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Recomendado por</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Sin solicitudes.</td></tr>
                )}
                {requests.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-3 py-2 text-gray-500 text-xs">{new Date(r.created_at).toLocaleString('es-ES')}</td>
                    <td className="px-3 py-2 font-medium">{r.name}</td>
                    <td className="px-3 py-2 text-gray-600">{r.email}</td>
                    <td className="px-3 py-2 text-gray-600">{r.recommended_by || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[r.status] || 'bg-gray-100'}`}>
                        {STATUS_LABELS[r.status] || r.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <button onClick={() => viewDetail(r.id)} data-testid={`onboarding-view-${r.id}`} className="text-xs text-purple-600 hover:underline">Ver</button>
                      {(r.status === 'pending' || r.status === 'info_requested') && (
                        <>
                          <button onClick={() => openApprove(r)} data-testid={`onboarding-approve-${r.id}`} className="text-xs px-2 py-1 rounded bg-emerald-600 text-white">Aprobar</button>
                          <button onClick={() => openInfo(r)} data-testid={`onboarding-info-${r.id}`} className="text-xs px-2 py-1 rounded bg-blue-600 text-white">Info</button>
                          <button onClick={() => openReject(r)} data-testid={`onboarding-reject-${r.id}`} className="text-xs px-2 py-1 rounded bg-red-600 text-white">Rechazar</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* --- Codigos --- */}
      {tab === 'codes' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full text-sm" data-testid="onboarding-codes-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left">Codigo</th>
                <th className="px-3 py-2 text-left">Correo vinculado</th>
                <th className="px-3 py-2 text-left">Emitido</th>
                <th className="px-3 py-2 text-left">Expira</th>
                <th className="px-3 py-2 text-left">Usos</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-500">Sin codigos emitidos.</td></tr>
              )}
              {codes.map((c) => {
                const isActive = !c.revoked && c.uses < (c.max_uses || 1);
                return (
                  <tr key={c.id} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{c.code}</td>
                    <td className="px-3 py-2 text-gray-600">{c.bound_email}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{c.issued_at ? new Date(c.issued_at).toLocaleDateString('es-ES') : '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('es-ES') : 'Sin expiracion'}</td>
                    <td className="px-3 py-2 text-xs">{c.uses}/{c.max_uses || 1}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${c.revoked ? 'bg-red-100 text-red-700' : (c.uses > 0 ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700')}`}>
                        {c.revoked ? 'Revocado' : (c.uses > 0 ? 'Usado' : 'Activo')}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isActive && (
                        <button onClick={() => revokeCode(c.id)} data-testid={`onboarding-revoke-${c.id}`} className="text-xs text-red-600 hover:underline">Revocar</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* --- Arbol de genealogia --- */}
      {tab === 'tree' && tree && (
        <div className="bg-white rounded-lg shadow p-4" data-testid="onboarding-tree">
          <p className="text-xs text-gray-500 mb-3">
            Arbol reconstruido a partir del campo <code className="text-purple-700">users.onboarding.invited_by_email</code>.
            Las generaciones indican distancia al Soberano.
          </p>
          {renderTreeNode(tree)}
        </div>
      )}

      {/* --- Herramientas --- */}
      {tab === 'tools' && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4" data-testid="onboarding-tools">
          <div>
            <h4 className="font-semibold text-gray-900">Marcar fundadores existentes (Generacion 0)</h4>
            <p className="text-sm text-gray-600 mt-1">
              Marca a todos los usuarios ya registrados sin datos de incorporacion como fundadores del ecosistema,
              vinculados directamente al Soberano. Idempotente: solo actualiza los que aun no tienen el campo.
            </p>
            <button onClick={backfillFounders} data-testid="onboarding-backfill-button"
              className="mt-3 px-4 py-2 rounded bg-purple-600 text-white text-sm font-medium">
              Ejecutar backfill de fundadores
            </button>
            {backfillResult && (
              <p className="text-xs text-emerald-700 mt-2">{backfillResult.message}</p>
            )}
          </div>
        </div>
      )}

      {/* --- Modal accion --- */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="onboarding-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {modal.type === 'approve' && `Aprobar solicitud de ${modal.req.name}`}
              {modal.type === 'reject' && `Rechazar solicitud de ${modal.req.name}`}
              {modal.type === 'info' && `Solicitar informacion adicional a ${modal.req.name}`}
            </h3>
            <p className="text-xs text-gray-500 mb-4">{modal.req.email}</p>

            {modal.type === 'approve' && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Notas internas (opcional)</label>
                  <textarea rows={2} value={modalForm.admin_notes || ''} onChange={(e) => setModalForm({ ...modalForm, admin_notes: e.target.value })}
                    data-testid="onboarding-modal-notes"
                    className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Dias hasta expirar</label>
                    <input type="number" min={1} value={modalForm.expires_days} onChange={(e) => setModalForm({ ...modalForm, expires_days: parseInt(e.target.value, 10) })}
                      data-testid="onboarding-modal-expires"
                      className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Usos maximos</label>
                    <input type="number" min={1} value={modalForm.max_uses} onChange={(e) => setModalForm({ ...modalForm, max_uses: parseInt(e.target.value, 10) })}
                      data-testid="onboarding-modal-max-uses"
                      className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                  </div>
                </div>
              </div>
            )}
            {modal.type === 'reject' && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Motivo del rechazo *</label>
                  <textarea rows={3} required value={modalForm.reason || ''} onChange={(e) => setModalForm({ ...modalForm, reason: e.target.value })}
                    data-testid="onboarding-modal-reason"
                    className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium">Notas internas (opcional)</label>
                  <textarea rows={2} value={modalForm.admin_notes || ''} onChange={(e) => setModalForm({ ...modalForm, admin_notes: e.target.value })}
                    className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                </div>
              </div>
            )}
            {modal.type === 'info' && (
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">Mensaje / que se pide *</label>
                  <textarea rows={3} required value={modalForm.message || ''} onChange={(e) => setModalForm({ ...modalForm, message: e.target.value })}
                    data-testid="onboarding-modal-message"
                    className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium">Notas internas (opcional)</label>
                  <textarea rows={2} value={modalForm.admin_notes || ''} onChange={(e) => setModalForm({ ...modalForm, admin_notes: e.target.value })}
                    className="mt-1 w-full px-2 py-1 border rounded text-sm" />
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={closeModal} data-testid="onboarding-modal-cancel" className="px-3 py-1.5 rounded text-sm bg-gray-100 text-gray-700">Cancelar</button>
              <button onClick={submitModal} disabled={busy}
                data-testid="onboarding-modal-submit"
                className={`px-3 py-1.5 rounded text-sm text-white ${modal.type === 'approve' ? 'bg-emerald-600' : modal.type === 'reject' ? 'bg-red-600' : 'bg-blue-600'}`}>
                {busy ? '...' : (modal.type === 'approve' ? 'Aprobar y generar codigo' : modal.type === 'reject' ? 'Rechazar' : 'Enviar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Modal detalle --- */}
      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="onboarding-detail-modal">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{detail.request.name}</h3>
                <p className="text-xs text-gray-500">{detail.request.email} · {detail.request.phone || 'sin telefono'}</p>
              </div>
              <button onClick={() => setDetail(null)} data-testid="onboarding-detail-close" className="text-gray-400 hover:text-gray-700">✕</button>
            </div>
            <div className="text-sm space-y-2">
              <p><span className="font-medium">Estado:</span> <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[detail.request.status]}`}>{STATUS_LABELS[detail.request.status] || detail.request.status}</span></p>
              <p><span className="font-medium">Recomendado por:</span> {detail.request.recommended_by || '—'} {detail.request.relationship ? `(${detail.request.relationship})` : ''}</p>
              <p><span className="font-medium">Motivacion:</span></p>
              <p className="bg-gray-50 p-2 rounded text-gray-700">{detail.request.motivation}</p>
              {detail.request.rejection_reason && (
                <p className="text-red-700 text-xs"><span className="font-medium">Motivo del rechazo:</span> {detail.request.rejection_reason}</p>
              )}
              {detail.code && (
                <div className="bg-purple-50 p-3 rounded">
                  <p className="text-xs font-medium text-purple-800">Codigo emitido</p>
                  <p className="font-mono text-sm">{detail.code.code}</p>
                  <p className="text-xs text-gray-500">Emitido por {detail.code.issued_by} · Expira {detail.code.expires_at || 'nunca'} · Usos {detail.code.uses}/{detail.code.max_uses}</p>
                </div>
              )}
              <div>
                <p className="font-medium mt-3">Historial de estados</p>
                <ul className="text-xs text-gray-700 mt-1 space-y-1">
                  {(detail.request.status_history || []).map((h, i) => (
                    <li key={i} className="border-l-2 border-purple-300 pl-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${STATUS_COLORS[h.status] || 'bg-gray-100'}`}>{STATUS_LABELS[h.status] || h.status}</span>
                      <span className="ml-2">{new Date(h.at).toLocaleString('es-ES')}</span>
                      <span className="ml-2 text-gray-500">por {h.by}</span>
                      {h.notes && <div className="text-gray-600 italic mt-0.5">&quot;{h.notes}&quot;</div>}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-medium mt-3">Bitacora de acciones</p>
                <ul className="text-xs text-gray-600 mt-1 space-y-1">
                  {(detail.request.action_log || []).map((a, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-gray-400">{new Date(a.at).toLocaleString('es-ES')}</span>
                      <span className="font-mono text-purple-700">{a.action}</span>
                      <span className="text-gray-500">por {a.by}</span>
                      {a.notes && <span className="text-gray-600 italic">— {a.notes}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOnboardingPanel;
